import { useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { snackbar } from "../../../../utils/general/generalUtils"
import { deleteClientProtected } from "../../clients/clients"
import { LCT_DELETE_ALERT, TTR_DELETE_ALERT } from "../../urls/private"

interface IDeleteAlertHook {
    isSending: boolean
    error: IAppError | undefined
    deleteResponse: (
        deviceSystemRef: string,
        dataset?: Datasets
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a raise alert request
 * on a TTR or LCT device.
 * @returns {IDeleteAlertHook} - loading and error states.
 */
const useDeleteAlert = (): IDeleteAlertHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)

    const deleteResponse = async (
        deviceSystemRef: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            // Build response url
            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                await deleteClientProtected(
                    LCT_DELETE_ALERT(deviceSystemRef, dataset)
                )
            } else {
                await deleteClientProtected(TTR_DELETE_ALERT(deviceSystemRef))
            }
            snackbar("Alert successfully deleted", "success")
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            snackbar(errorObj.displayDetail, "error")
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        deleteResponse,
    }
}

export default useDeleteAlert
